import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BG = "https://images.unsplash.com/photo-1454117096348-e4abbeba002c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMGNsZWFufGVufDB8fHx8MTc4NDY2NDQ1OXww&ixlib=rb-4.1.0&q=85";

const DEMO = [
  { label: "Client Admin", email: "admin@meridian.com", password: "Passw0rd!" },
  { label: "Manager", email: "manager@meridian.com", password: "Passw0rd!" },
  { label: "Employee", email: "employee@meridian.com", password: "Passw0rd!" },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/app", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome to Foresee");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-[46%] flex-col justify-between overflow-hidden bg-[hsl(226,64%,11%)] p-12 lg:flex">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(226,64%,11%)] via-[hsl(226,64%,13%)]/90 to-[hsl(236,80%,40%)]/70" />
        <div className="relative z-10"><Logo light /></div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight text-white">
            The operational compliance platform your organisation runs on.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/70">
            Health &amp; Safety, ISO, ESG, Training, Fire Safety and Contractor management — unified, intelligent and enterprise-grade.
          </p>
          <div className="mt-8 flex gap-6 text-white/80">
            {["12 modules", "AI-driven", "Multi-tenant"].map((t) => <div key={t} className="text-sm font-medium">{t}</div>)}
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/40">Foresee · Beyond Compliance — SEQ Group · Amden · FSS</div>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Sign in to your workspace</h2>
          <p className="mt-1 text-sm text-muted-foreground">Enter your credentials to continue.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full text-sm font-semibold" data-testid="login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
          </form>

          <a href="https://www.amden.ie/contact" target="_blank" rel="noreferrer"
            data-testid="register-contact-link"
            className="mt-4 flex items-center justify-center gap-1.5 rounded-full border border-border py-2.5 text-sm font-medium text-foreground transition-colors hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]">
            New to Foresee? Contact us <ExternalLink className="h-3.5 w-3.5" />
          </a>

          <div className="mt-7 rounded-2xl border border-border bg-secondary/50 p-4">
            <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Demo accounts — click to fill</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.email} onClick={() => setForm({ email: d.email, password: d.password })} type="button"
                  data-testid={`demo-${d.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="rounded-lg border border-border bg-card px-3 py-2 text-left text-xs transition-colors hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]">
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
